import React, { useState, useEffect } from 'react';
import AddVehicleModal from '../components/AddVehicleModal';
import UpdateVehicleModal from '../components/UpdateVehicleModal';
import ConfirmationModal from '../components/ConfirmationModal';
import ToastContainer from '../components/ToastContainer';
import { apiService, Vehicle } from '../services/api';
import { exportVehiclesToCSV } from '../utils/csvExport';

interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

const ManageVehicles: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);


  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getVehicles();
      console.log('ManageVehicles - Vehicles response:', response);
      
      if (response.success && response.data) {
        console.log('ManageVehicles - Full response:', response);
        console.log('ManageVehicles - Response data:', response.data);
        
        // The API response has nested structure: response.data.data contains the vehicles array
        const apiResponse = response.data as any;
        const vehiclesArray = apiResponse.data || apiResponse;
        
        console.log('ManageVehicles - Vehicles array:', vehiclesArray);
        
        // Ensure vehicles is always an array
        const vehiclesData = Array.isArray(vehiclesArray) ? vehiclesArray : [];
        console.log('ManageVehicles - Setting vehicles:', vehiclesData);
        
        // Debug: Log the first vehicle's structure to see field names
        if (vehiclesData.length > 0) {
          console.log('ManageVehicles - First vehicle structure:', vehiclesData[0]);
          console.log('ManageVehicles - Available fields:', Object.keys(vehiclesData[0]));
            console.log('ManageVehicles - vehicleRCNumber value:', vehiclesData[0].vehicleRCNumber);
          console.log('ManageVehicles - All vehicle data:', vehiclesData);
        }
        
        setVehicles(vehiclesData);
      } else {
        setError(response.error || 'Failed to load vehicles');
        setVehicles([]); // Set empty array on failure
      }
    } catch (error) {
      console.error('ManageVehicles - Error loading vehicles:', error);
      setError('Failed to load vehicles');
      setVehicles([]); // Ensure array is set even on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddVehicle = (newVehicle: Vehicle) => {
    console.log('ManageVehicles - New vehicle added:', newVehicle);
    addToast('Vehicle added successfully', 'success');
    // Refresh the vehicles list to get the latest data from server
    loadVehicles();
  };

  const handleUpdateVehicle = (updatedVehicle: Vehicle) => {
    console.log('ManageVehicles - Vehicle updated:', updatedVehicle);
    addToast('Vehicle updated successfully', 'success');
    // Refresh the vehicles list to get the latest data from server
    loadVehicles();
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsUpdateModalOpen(true);
  };

  const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleDeleteClick = (id: string) => {
    setVehicleToDelete(id);
    setShowConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!vehicleToDelete) return;

    try {
      const response = await apiService.deleteVehicle(vehicleToDelete);
      if (response.success) {
        setVehicles(vehicles.filter(vehicle => vehicle._id !== vehicleToDelete));
        addToast('Vehicle deleted successfully', 'success');
      } else {
        addToast(response.error || 'Failed to delete vehicle', 'error');
      }
    } catch (error) {
      addToast('An unexpected error occurred while deleting the vehicle', 'error');
    } finally {
      setShowConfirmModal(false);
      setVehicleToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowConfirmModal(false);
    setVehicleToDelete(null);
  };

  const handleExportVehicles = () => {
    if (vehicles.length === 0) {
      addToast('No vehicles to export', 'warning');
      return;
    }
    exportVehiclesToCSV(vehicles);
    addToast('Vehicles exported successfully', 'success');
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-100 min-h-full">
      {/* Page Title */}

      {/* Action Buttons */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
      <div className="mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Vehicles Management</h1>
      </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="px-4 py-2 bg-jd-green text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
          >
            <span>+</span>
            <span>Add Vehicle</span>
          </button>
          {/* <button 
            onClick={handleExportVehicles}
            className="px-4 py-2 bg-blue-100 text-jd-blue rounded-lg hover:bg-blue-200 transition-colors"
          >
            Export List
          </button> */}
          <button 
          onClick={handleExportVehicles}
          className="inline-flex items-center px-4 py-2 bg-blue-100 text-jd-blue rounded-lg hover:bg-blue-200 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export
        </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-gray-500">Loading vehicles...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button 
            onClick={loadVehicles}
            className="px-4 py-2 bg-jd-green text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Vehicle Table - Mobile Card View */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 block sm:hidden">
          {!Array.isArray(vehicles) || vehicles.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No vehicles found. Add your first vehicle to get started.
            </div>
          ) : (
            vehicles.map((vehicle, index) => (
              <div key={vehicle._id || index} className="p-4 border-b border-gray-200 last:border-b-0">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-gray-900">{vehicle.vehicleNumber}</h3>
                    <p className="text-sm text-gray-500">{vehicle.vehicleType}</p>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    vehicle.status === 'Active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {vehicle.status || 'Active'}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">RC No: </span>
                    <span className="text-gray-600">{vehicle.vehicleRCNumber || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Driver: </span>
                    <span className="text-gray-600">{vehicle.assignedDriver || 'Not Assigned'}</span>
                  </div>
                  <div className="pt-2">
                    <button 
                      onClick={() => handleEditVehicle(vehicle)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-4"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => vehicle._id && handleDeleteClick(vehicle._id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Vehicle Table - Desktop View */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 hidden sm:block">
          {!Array.isArray(vehicles) || vehicles.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No vehicles found. Add your first vehicle to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plate No.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      RC No.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned Driver
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vehicles.map((vehicle, index) => (
                    <tr key={vehicle._id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {vehicle.vehicleNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vehicle.vehicleType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vehicle.vehicleRCNumber || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vehicle.assignedDriver || 'Not Assigned'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          vehicle.status === 'Active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {vehicle.status || 'Active'}
                        </span>
                      </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button 
                        onClick={() => handleEditVehicle(vehicle)}
                        className="text-blue-600 hover:text-blue-800 mr-4"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => vehicle._id && handleDeleteClick(vehicle._id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Vehicle Modal */}
      <AddVehicleModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddVehicle={handleAddVehicle}
      />

      <UpdateVehicleModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedVehicle(null);
        }}
        onUpdateVehicle={handleUpdateVehicle}
        vehicle={selectedVehicle}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Vehicle"
        message="Are you sure you want to delete this vehicle? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Toast Container */}
      <ToastContainer
        toasts={toasts}
        onRemoveToast={removeToast}
      />
    </div>
  );
};

export default ManageVehicles;
